import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

/**
 * Drag-to-reorder for one row of a vertical list. Attach `rowRef` to the row
 * and `handleRef` to its grip; rows swap live as the pointer crosses a
 * neighbour's midline, so `move(from, to)` fires repeatedly during one drag.
 */
export default function useDragReorder(type, index, move) {
  const rowRef = useRef(null);
  const handleRef = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type,
    item: { index },
    // Rows are keyed by index, so after a swap the dragged entry is rendered
    // by a different row than the one the drag started on.
    isDragging: (monitor) => monitor.getItem().index === index,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: type,
    hover: (item, monitor) => {
      if (!rowRef.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const rect = rowRef.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;
      const clientY = monitor.getClientOffset().y - rect.top;
      if (dragIndex < hoverIndex && clientY < middleY) return;
      if (dragIndex > hoverIndex && clientY > middleY) return;

      move(dragIndex, hoverIndex);
      // react-dnd requires mutating the drag item so the next hover compares
      // against the new index.
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
  });

  preview(drop(rowRef));
  drag(handleRef);

  return { rowRef, handleRef, isDragging };
}
